'use client'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight'
import { CheckIcon } from '@phosphor-icons/react/dist/ssr/Check'
import { DotsThreeVerticalIcon } from '@phosphor-icons/react/dist/ssr/DotsThreeVertical'
import { EyeIcon } from '@phosphor-icons/react/dist/ssr/Eye'
import { PlusSquareIcon } from '@phosphor-icons/react/dist/ssr/PlusSquare'
import { SquareSplitHorizontalIcon } from '@phosphor-icons/react/dist/ssr/SquareSplitHorizontal'
import { ArrowCounterClockwiseIcon } from '@phosphor-icons/react/dist/ssr/ArrowCounterClockwise'
import { ArrowUUpLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowUUpLeft'
import { UsersThreeIcon } from '@phosphor-icons/react/dist/ssr/UsersThree'
import { Button, DropdownMenu, Input, ScrollArea } from '@skop-app/lib-front'
import { DynamicForm } from '@skop-app/lib-front/dynamic-form'
import type { Field } from '@skop-app/lib-domain/types'
import { useTranslations } from 'next-intl'
import { cx } from 'class-variance-authority'
import { z } from 'zod/v4'

import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { Dialog } from '@/components/atoms/Dialog'
import { Link } from '@/navigation'
import {
  GridViewWriteError,
  type GridViewWriteErrorCode,
} from '@/services/mutations/gridView'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/atoms/Popover'

import { useGridApi, useGridViews } from './GridRoot'
import { usePreventGridFocusSteal } from './usePreventGridFocusSteal'

type ViewFormMode = 'save-as' | 'edit-meta' | null
type ViewSectionKey = 'personal' | 'skop' | 'team'
type ViewFormValues = {
  name: string
  description: string
  setAsTeamDefault: boolean
}

const GRID_VIEW_TEAM_FORM_ID = 'grid-view-team-form'

const gridViewWriteErrorMessages: Record<
  GridViewWriteErrorCode,
  | 'GridView.sharedViewWriteErrorUnauthorized'
  | 'GridView.sharedViewWriteErrorBadRequest'
  | 'GridView.sharedViewWriteErrorConflict'
  | 'GridView.sharedViewWriteError'
> = {
  Unauthorized: 'GridView.sharedViewWriteErrorUnauthorized',
  BadRequest: 'GridView.sharedViewWriteErrorBadRequest',
  Conflict: 'GridView.sharedViewWriteErrorConflict',
  Unknown: 'GridView.sharedViewWriteError',
}

const getGridViewWriteErrorCode = (error: unknown): GridViewWriteErrorCode =>
  error instanceof GridViewWriteError ? error.code : 'Unknown'

const normalizeSearchText = (value: string) => value.trim().toLocaleLowerCase()

const getStringInputValue = (value: unknown) => {
  if (value && typeof value === 'object' && 'target' in value) {
    const target = value.target as { value?: unknown } | null
    return typeof target?.value === 'string' ? target.value : ''
  }

  return typeof value === 'string' ? value : ''
}

const getInitialViewFormValues = ({
  defaultName,
  defaultDescription,
}: {
  defaultName?: string | null
  defaultDescription?: string | null
}): ViewFormValues => ({
  name: defaultName ?? '',
  description: defaultDescription ?? '',
  setAsTeamDefault: false,
})

const getCurrentViewLabel = ({
  currentViewKind,
  currentSharedViewName,
  currentSystemViewName,
  viewSource,
  t,
}: {
  currentViewKind: 'personal' | 'system' | 'team'
  currentSharedViewName: string | null
  currentSystemViewName?: string | null
  viewSource?:
    | 'personal-draft'
    | 'selected-view'
    | 'default-view'
    | 'definition'
    | 'system-preset'
    | 'system'
  t: ReturnType<typeof useTranslations>
}) => {
  if (viewSource === 'definition') {
    return t('GridView.definitionDefault')
  }
  if (currentViewKind === 'team' && currentSharedViewName) {
    return currentSharedViewName
  }
  if (currentViewKind === 'system') {
    return currentSystemViewName ?? t('GridView.systemDefault')
  }
  return t('GridView.personalDisplay')
}

function ViewSection({
  title,
  count,
  open,
  testId,
  onToggle,
  children,
}: {
  title: string
  count: number
  open: boolean
  testId?: string
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <section
      className="border-t border-zinc-100 first:border-t-0"
      data-testid={testId}
    >
      <button
        type="button"
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-3 pb-1.5 pt-3 text-left transition hover:bg-zinc-50"
        onClick={onToggle}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {title}
        </span>
        <span className="flex items-center gap-2 text-xs font-medium text-slate-400">
          <span className="sr-only">{count}</span>
          {open ? (
            <CaretDownIcon className="size-3.5" />
          ) : (
            <CaretRightIcon className="size-3.5" />
          )}
        </span>
      </button>
      {open ? <div className="space-y-0.5 px-3 pb-3">{children}</div> : null}
    </section>
  )
}

function EmptyState({
  title,
  description,
  actionLabel,
  actionDisabled,
  role,
  onAction,
}: {
  title: string
  description?: string
  actionLabel?: string
  actionDisabled?: boolean
  role?: 'status'
  onAction?: () => void
}) {
  return (
    <div
      className="flex min-w-0 gap-2.5 rounded-md px-2 py-2"
      role={role}
      aria-live={role === 'status' ? 'polite' : undefined}
    >
      <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center text-slate-400">
        <UsersThreeIcon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold leading-4 text-slate-700">
          {title}
        </p>
        {description ? (
          <p className="mt-1 text-xs leading-4 text-slate-500">
            {description}
            {actionLabel && onAction ? (
              <>
                {' '}
                <button
                  type="button"
                  disabled={actionDisabled}
                  className="font-semibold text-primary-800 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={onAction}
                >
                  {actionLabel}
                </button>
              </>
            ) : null}
          </p>
        ) : null}
        {!description && actionLabel && onAction ? (
          <button
            type="button"
            disabled={actionDisabled}
            className="mt-1 text-xs font-semibold text-primary-800 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onAction}
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function ViewRow({
  title,
  description,
  meta,
  badge,
  active,
  disabled,
  testId,
  onSelect,
  actions,
}: {
  title: string
  description?: string | null
  meta?: string | null
  badge?: string | null
  active?: boolean
  disabled?: boolean
  testId?: string
  onSelect: () => void
  actions?: React.ReactNode
}) {
  return (
    <div
      data-grid-view-name={title}
      data-grid-view-active={active ? 'true' : 'false'}
      data-testid={testId}
      className={cx(
        'group relative rounded-md transition',
        active ? 'bg-primary-100/35' : 'hover:bg-zinc-50',
      )}
    >
      <button
        type="button"
        disabled={disabled}
        className="flex w-full min-w-0 gap-2.5 rounded-md px-2 py-2 pr-9 text-left disabled:cursor-not-allowed disabled:opacity-50"
        onClick={onSelect}
      >
        <span
          aria-hidden
          className={cx(
            'mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border-2',
            active ? 'border-primary-800' : 'border-zinc-300',
          )}
        >
          {active ? (
            <span className="size-1.5 rounded-full bg-primary-800" />
          ) : null}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-2">
            <span
              title={title}
              className={cx(
                'min-w-0 truncate text-[13px] font-semibold leading-4',
                active ? 'text-primary-900' : 'text-slate-700',
              )}
            >
              {title}
            </span>
            {badge ? (
              <span
                title={badge}
                className="ml-auto shrink-0 rounded border border-primary-200 bg-white/70 px-1.5 py-px text-[10px] font-medium leading-4 text-primary-900"
              >
                {badge}
              </span>
            ) : null}
          </span>
          {description ? (
            <span
              title={description}
              className="mt-1 line-clamp-2 text-xs leading-4 text-slate-500"
            >
              {description}
            </span>
          ) : null}
          {meta ? (
            <span className="mt-1 text-[11px] leading-4 text-slate-400">
              {meta}
            </span>
          ) : null}
        </span>
      </button>
      {actions ? (
        <div className="absolute right-2.5 top-2.5 z-10">{actions}</div>
      ) : null}
    </div>
  )
}

function ActionRow({
  icon,
  children,
  disabled,
  title,
  testId,
  onClick,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  disabled?: boolean
  title?: string
  testId?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      title={title}
      className="group flex h-8 w-full items-center gap-2.5 rounded-sm px-2 text-left text-[13px] font-medium text-slate-700 transition-colors hover:bg-zinc-50 focus-visible:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:text-slate-400 disabled:opacity-75 disabled:hover:bg-transparent"
      onClick={onClick}
    >
      <span className="flex size-4 shrink-0 items-center justify-center text-slate-500 group-disabled:text-slate-400">
        {icon}
      </span>
      <span className="truncate">{children}</span>
    </button>
  )
}

function ViewFormDialog({
  mode,
  defaultName,
  defaultDescription,
  canSetDefaultTeam,
  onClose,
  onSubmit,
}: {
  mode: ViewFormMode
  defaultName?: string | null
  defaultDescription?: string | null
  canSetDefaultTeam: boolean
  onClose: () => void
  onSubmit: (params: {
    name: string
    description?: string
    setAsTeamDefault?: boolean
  }) => Promise<void>
}) {
  const t = useTranslations()
  const [formValues, setFormValues] = useState<ViewFormValues>(() =>
    getInitialViewFormValues({ defaultName, defaultDescription }),
  )
  const [isSaving, setIsSaving] = useState(false)
  const [submitError, setSubmitError] = useState<GridViewWriteErrorCode | null>(
    null,
  )

  useEffect(() => {
    setFormValues(getInitialViewFormValues({ defaultName, defaultDescription }))
    setSubmitError(null)
  }, [defaultDescription, defaultName, mode])

  const fields = useMemo<Field[]>(() => {
    const nextFields: Field[] = [
      {
        name: 'name',
        label: t('GridView.viewName'),
        type: 'text',
        autoComplete: 'off',
        placeholder: t('GridView.viewNamePlaceholder'),
        validation: z
          .string({
            error: t('ZodErrorMessages.mandatory'),
          })
          .trim()
          .min(1, {
            error: t('ZodErrorMessages.minChar', { min: 1 }),
          }),
        className: 'col-span-12',
        onChange: (value: unknown) => {
          setFormValues((current) => ({
            ...current,
            name: getStringInputValue(value),
          }))
        },
      },
      {
        name: 'description',
        label: t('GridView.viewDescription'),
        type: 'textarea',
        autoComplete: 'off',
        placeholder: t('GridView.viewDescriptionPlaceholder'),
        rows: 3,
        validation: z.string().optional().or(z.literal('')),
        className: 'col-span-12',
        onChange: (value: unknown) => {
          setFormValues((current) => ({
            ...current,
            description: getStringInputValue(value),
          }))
        },
      },
    ]

    if (mode === 'save-as' && canSetDefaultTeam) {
      nextFields.push({
        name: 'setAsTeamDefault',
        label: t('GridView.setDefaultOnCreate'),
        type: 'switch',
        'aria-label': t('GridView.setDefaultOnCreate'),
        validation: z.boolean().optional(),
        className: 'col-span-12',
        onChange: (checked: unknown) => {
          setFormValues((current) => ({
            ...current,
            setAsTeamDefault: Boolean(checked),
          }))
        },
      })
    }

    return nextFields
  }, [canSetDefaultTeam, mode, t])

  const isOpen = mode !== null
  const canSubmit = !isSaving && formValues.name.trim().length > 0
  const handleSubmit = async (values: Record<string, unknown>) => {
    setSubmitError(null)
    setIsSaving(true)
    try {
      await onSubmit({
        name: String(values.name ?? '').trim(),
        description: String(values.description ?? '').trim() || undefined,
        setAsTeamDefault: Boolean(values.setAsTeamDefault),
      })
      onClose()
    } catch (error) {
      setSubmitError(getGridViewWriteErrorCode(error))
    } finally {
      setIsSaving(false)
    }
  }

  if (!mode) return null

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content
        className="max-w-lg"
        data-testid="grid-view-team-form-dialog"
      >
        <Dialog.Title>
          {mode === 'save-as'
            ? t('GridView.saveAsSharedView')
            : t('GridView.editSharedView')}
        </Dialog.Title>
        {submitError ? (
          <p role="alert" className="px-4 pt-3 text-sm leading-5 text-red-700">
            {t(gridViewWriteErrorMessages[submitError])}
          </p>
        ) : null}
        <DynamicForm
          id={GRID_VIEW_TEAM_FORM_ID}
          fields={fields}
          values={formValues}
          onSubmit={handleSubmit}
          className="px-4 py-4"
        />
        <Dialog.Footer className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('Common.cancel')}
          </Button>
          <Button
            type="submit"
            form={GRID_VIEW_TEAM_FORM_ID}
            disabled={!canSubmit}
          >
            {mode === 'save-as' ? t('Common.create') : t('Common.save')}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

function DeleteViewDialog({
  viewName,
  onClose,
  onConfirm,
}: {
  viewName: string | null
  onClose: () => void
  onConfirm: () => Promise<void>
}) {
  const t = useTranslations()
  const [isDeleting, setIsDeleting] = useState(false)
  const [submitError, setSubmitError] = useState<GridViewWriteErrorCode | null>(
    null,
  )
  const handleClose = () => {
    setSubmitError(null)
    onClose()
  }

  return (
    <Dialog.Root
      open={Boolean(viewName)}
      onOpenChange={(open) => !open && !isDeleting && handleClose()}
    >
      <Dialog.Content
        className="max-w-md"
        data-testid="grid-view-delete-dialog"
      >
        <Dialog.Title>{t('GridView.deleteSharedViewTitle')}</Dialog.Title>
        <Dialog.Description>
          {t('GridView.deleteSharedViewDescription', {
            name: viewName ?? '',
          })}
        </Dialog.Description>
        {submitError ? (
          <p role="alert" className="px-4 pt-3 text-sm leading-5 text-red-700">
            {t(gridViewWriteErrorMessages[submitError])}
          </p>
        ) : null}
        <Dialog.Footer className="flex justify-end gap-2">
          <Button variant="ghost" onClick={handleClose} disabled={isDeleting}>
            {t('Common.cancel')}
          </Button>
          <Button
            disabled={isDeleting}
            onClick={async () => {
              setSubmitError(null)
              setIsDeleting(true)
              try {
                await onConfirm()
                handleClose()
              } catch (error) {
                setSubmitError(getGridViewWriteErrorCode(error))
              } finally {
                setIsDeleting(false)
              }
            }}
          >
            {t('Common.delete')}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

function TeamViewActions({
  viewName,
  disabled,
  canEdit,
  canDelete,
  canSetDefault,
  isDefault,
  onEdit,
  onDelete,
  onSetDefault,
}: {
  viewName: string
  disabled: boolean
  canEdit: boolean
  canDelete: boolean
  canSetDefault: boolean
  isDefault?: boolean | null
  onEdit: () => void
  onDelete: () => void
  onSetDefault: () => void
}) {
  const t = useTranslations()
  const hasActions = canEdit || canDelete || canSetDefault

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          data-testid="grid-view-team-row-actions"
          disabled={disabled}
          className="flex size-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white/90 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={t('GridView.viewActionsFor', { name: viewName })}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <DotsThreeVerticalIcon className="size-4" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="end"
        sideOffset={6}
        className="z-[80] w-48 bg-white"
      >
        {canEdit ? (
          <DropdownMenu.Item
            data-testid="grid-view-team-action-edit"
            onClick={onEdit}
          >
            {t('GridView.editShort')}
          </DropdownMenu.Item>
        ) : null}
        {canSetDefault ? (
          <DropdownMenu.Item
            data-testid="grid-view-team-action-default"
            disabled={Boolean(isDefault)}
            onClick={onSetDefault}
          >
            {t('GridView.setDefaultShort')}
          </DropdownMenu.Item>
        ) : null}
        {canDelete ? (
          <DropdownMenu.Item
            data-testid="grid-view-team-action-delete"
            onClick={onDelete}
          >
            {t('Common.delete')}
          </DropdownMenu.Item>
        ) : null}
        {!hasActions ? (
          <DropdownMenu.Item disabled>
            {t('GridView.insufficientRights')}
          </DropdownMenu.Item>
        ) : null}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export function GridViewMenuItems({ onClose }: { onClose?: () => void }) {
  const t = useTranslations()
  const { isImpersonating } = useSessionRuntime()
  const views = useGridViews()
  const { api, gridIsReady, requestColumnsPanel } = useGridApi()
  const isInteractionBlocked = views?.isInteractionBlocked === true
  const [formMode, setFormMode] = useState<ViewFormMode>(null)
  const [editViewId, setEditViewId] = useState<string | null>(null)
  const [deleteViewId, setDeleteViewId] = useState<string | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [openSections, setOpenSections] = useState<
    Record<ViewSectionKey, boolean>
  >({
    personal: true,
    skop: true,
    team: true,
  })

  const editViewIdOrCurrent = editViewId ?? views?.currentSharedViewId ?? null
  const editView =
    views?.sharedViews.find((view) => view.id === editViewIdOrCurrent) ?? null
  const deleteView =
    views?.sharedViews.find((view) => view.id === deleteViewId) ?? null

  useLayoutEffect(() => {
    requestColumnsPanel()
  }, [requestColumnsPanel])

  const openColumnsPanel = useCallback(() => {
    if (!api || !gridIsReady || isInteractionBlocked) return
    api.setSideBarVisible(true)
    api.openToolPanel('columns')
    onClose?.()
  }, [api, gridIsReady, isInteractionBlocked, onClose])

  if (!views?.enabled) return null

  const currentCanEdit = views.currentSharedViewId
    ? views.canEditSharedView(views.currentSharedViewId)
    : false
  const normalizedSearch = normalizeSearchText(searchValue)
  const matchesSearch = (view: { name: string; description?: string | null }) =>
    normalizedSearch.length === 0 ||
    normalizeSearchText(`${view.name} ${view.description ?? ''}`).includes(
      normalizedSearch,
    )
  const personalView = views.hasPersonalView
    ? {
        name: t('GridView.personalDisplay'),
        description: t('GridView.personalDraftHelp'),
      }
    : null
  const filteredPersonalViews =
    personalView && matchesSearch(personalView) ? [personalView] : []
  const filteredSystemViews = views.systemViews.filter(matchesSearch)
  const filteredSharedViews = views.sharedViews.filter(matchesSearch)
  const totalViewCount =
    (personalView ? 1 : 0) + views.systemViews.length + views.sharedViews.length
  const isPreferenceWriteBlocked =
    views.writeState === 'conflict' || views.writeState === 'error'
  const isViewActionDisabled =
    isImpersonating ||
    views.isLoading ||
    views.readState === 'error' ||
    isPreferenceWriteBlocked ||
    isInteractionBlocked
  const sharedViewsReadError =
    views.readState === 'error' ? (
      <EmptyState
        role="status"
        title={t('GridView.sharedViewsUnavailable')}
        description={t('GridView.sharedViewsUnavailableHelp')}
        actionLabel={t('Common.retry')}
        onAction={() => void views.retrySharedViews()}
      />
    ) : null
  const canSaveAsSharedView =
    views.canCreateSharedView && views.isModified && !isViewActionDisabled
  const defaultSystemView =
    views.systemViews.find((view) => view.isDefault) ?? null
  const isUsingDefinitionDefault = views.viewSource === 'definition'
  const isViewingDefault = views.hasDefinitionDefault
    ? isUsingDefinitionDefault
    : !views.hasPersonalDraft &&
      !views.isModified &&
      views.currentViewKind === 'system' &&
      (defaultSystemView
        ? views.currentSystemViewKey === defaultSystemView.key
        : !views.currentSystemViewKey)
  const saveAsSharedViewTitle = views.isLoading
    ? t('Common.loading')
    : !views.canCreateSharedView
      ? t('GridView.readonlyHelp')
      : !views.isModified
        ? t('GridView.noChangesHelp')
        : undefined
  const restoreSkopDefaultTitle = views.isLoading
    ? t('Common.loading')
    : isViewingDefault
      ? views.hasDefinitionDefault
        ? t('GridView.alreadyOnDefinitionDefaultHelp')
        : t('GridView.alreadyOnSkopDefaultHelp')
      : undefined
  const restoreSystemDefaultLabel = views.hasDefinitionDefault
    ? t('GridView.restoreDefinitionDefault')
    : t('GridView.restoreSkopDefault')
  const setSectionOpen = (section: ViewSectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }))
  }

  return (
    <div
      className="w-[340px] max-w-[calc(100vw-24px)] overflow-hidden rounded-md bg-white text-slate-900"
      data-testid="grid-view-menu"
      data-grid-view-read-state={views.readState}
    >
      {totalViewCount > 4 ? (
        <div className="border-b border-zinc-100 px-3 py-2.5">
          <Input
            aria-label={t('GridView.searchPlaceholder')}
            name="grid-view-search"
            autoComplete="off"
            value={searchValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSearchValue(event.target.value)
            }
            placeholder={t('GridView.searchPlaceholder')}
            className="!h-9 !w-full"
          />
        </div>
      ) : null}

      {views.writeState === 'conflict' ? (
        <div className="border-b border-amber-100 bg-amber-50 px-3 py-2.5">
          <p className="text-xs leading-4 text-amber-900">
            {t('GridView.preferenceConflict')}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              disabled={views.readState === 'error' || isImpersonating}
              onClick={() => void views.resolvePreferenceConflict('local')}
            >
              {t('GridView.keepLocalChanges')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={views.readState === 'error' || isImpersonating}
              onClick={() => void views.resolvePreferenceConflict('remote')}
            >
              {t('GridView.useRemoteVersion')}
            </Button>
          </div>
        </div>
      ) : views.writeState === 'error' ? (
        <div className="border-b border-amber-100 bg-amber-50 px-3 py-2.5">
          <p className="text-xs leading-4 text-amber-900">
            {t('GridView.preferenceWriteError')}
          </p>
          <Button
            size="sm"
            variant="secondary"
            className="mt-2"
            disabled={views.readState === 'error' || isImpersonating}
            onClick={() => void views.retryPreferenceWrite()}
          >
            {t('Common.retry')}
          </Button>
        </div>
      ) : null}

      <ScrollArea className="max-h-[48vh]">
        <div>
          {personalView ? (
            <ViewSection
              title={t('GridView.personalViews')}
              count={filteredPersonalViews.length}
              open={openSections.personal}
              testId="grid-view-personal-section"
              onToggle={() => setSectionOpen('personal')}
            >
              {filteredPersonalViews.length === 0 ? (
                <EmptyState title={t('GridView.noMatchingViews')} />
              ) : (
                <ViewRow
                  testId="grid-view-personal-row"
                  title={personalView.name}
                  description={personalView.description}
                  badge={null}
                  active={views.hasPersonalDraft}
                  disabled={isViewActionDisabled}
                  onSelect={() => {
                    if (!views.hasPersonalDraft) {
                      void views.applyPersonalView()
                    }
                  }}
                />
              )}
            </ViewSection>
          ) : null}

          <ViewSection
            title={t('GridView.skopViews')}
            count={filteredSystemViews.length}
            open={openSections.skop}
            testId="grid-view-default-section"
            onToggle={() => setSectionOpen('skop')}
          >
            {views.systemViews.length === 0 ? (
              <EmptyState title={t('GridView.noCompatibleSkopViews')} />
            ) : filteredSystemViews.length === 0 ? (
              <EmptyState title={t('GridView.noMatchingViews')} />
            ) : (
              filteredSystemViews.map((view) => (
                <ViewRow
                  key={view.key}
                  testId="grid-view-system-row"
                  title={view.name}
                  description={view.description}
                  badge={null}
                  active={
                    view.key === views.currentSystemViewKey &&
                    !views.hasPersonalDraft
                  }
                  disabled={isViewActionDisabled || !view.isCompatible}
                  onSelect={() => {
                    if (
                      view.key !== views.currentSystemViewKey ||
                      views.hasPersonalDraft ||
                      views.isModified
                    ) {
                      void views.applySystemView(view.key)
                    }
                  }}
                />
              ))
            )}
          </ViewSection>

          <ViewSection
            title={t('GridView.sharedViews')}
            count={filteredSharedViews.length}
            open={openSections.team}
            testId="grid-view-team-section"
            onToggle={() => setSectionOpen('team')}
          >
            {views.readState === 'error' && views.sharedViews.length === 0 ? (
              sharedViewsReadError
            ) : views.readState === 'loading' &&
              views.sharedViews.length === 0 ? (
              <EmptyState title={t('Common.loading')} />
            ) : views.sharedViews.length === 0 ? (
              <EmptyState
                title={t('GridView.noSharedViews')}
                description={t('GridView.noSharedViewsHelp')}
                actionLabel={t('Toolbar.customize-columns')}
                actionDisabled={isInteractionBlocked}
                onAction={openColumnsPanel}
              />
            ) : filteredSharedViews.length === 0 ? (
              <>
                {sharedViewsReadError}
                <EmptyState title={t('GridView.noMatchingViews')} />
              </>
            ) : (
              <>
                {sharedViewsReadError}
                {filteredSharedViews.map((view) => {
                  const canEdit = views.canEditSharedView(view.id)
                  const canDelete = views.canDeleteSharedView(view.id)
                  const isDisabled = isViewActionDisabled || !view.isCompatible

                  return (
                    <ViewRow
                      key={view.id}
                      testId="grid-view-team-row"
                      title={view.name}
                      description={view.description}
                      meta={
                        view.isCompatible
                          ? null
                          : t('GridView.incompatibleSharedView')
                      }
                      badge={
                        view.isDefault && views.sharedViews.length > 1
                          ? t('GridView.teamDefaultBadge')
                          : null
                      }
                      active={view.id === views.currentSharedViewId}
                      disabled={isDisabled}
                      onSelect={() => {
                        if (
                          view.isCompatible &&
                          view.id !== views.currentSharedViewId
                        ) {
                          void views.applySharedView(view.id)
                        }
                      }}
                      actions={
                        <TeamViewActions
                          viewName={view.name}
                          disabled={isViewActionDisabled}
                          canEdit={canEdit}
                          canDelete={canDelete}
                          canSetDefault={
                            views.canSetDefaultSharedView && view.isCompatible
                          }
                          isDefault={view.isDefault}
                          onEdit={() => {
                            setEditViewId(view.id)
                            setFormMode('edit-meta')
                          }}
                          onDelete={() => setDeleteViewId(view.id)}
                          onSetDefault={() =>
                            void views.setDefaultSharedView(view.id)
                          }
                        />
                      }
                    />
                  )
                })}
              </>
            )}
          </ViewSection>
        </div>
      </ScrollArea>

      <div className="border-t border-zinc-100 bg-white py-2">
        <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
          {t('GridView.actions')}
        </p>
        {isUsingDefinitionDefault ? (
          <p className="px-3 pb-2 text-xs leading-4 text-slate-500">
            {t('GridView.definitionDefaultHelp')}{' '}
            <Link
              href="/references/properties"
              className="font-semibold text-primary-800 underline-offset-2 hover:underline"
              onClick={onClose}
            >
              {t('GridView.definitionDefaultLink')}
            </Link>
          </p>
        ) : null}
        <div className="space-y-0.5 px-2">
          <ActionRow
            icon={<SquareSplitHorizontalIcon className="size-4" />}
            testId="grid-view-customize-columns"
            disabled={!gridIsReady || isInteractionBlocked}
            title={!gridIsReady ? t('Common.loading') : undefined}
            onClick={openColumnsPanel}
          >
            {t('Toolbar.customize-columns')}
          </ActionRow>
          <ActionRow
            icon={<PlusSquareIcon className="size-4" />}
            testId="grid-view-save-as"
            disabled={!canSaveAsSharedView}
            title={saveAsSharedViewTitle}
            onClick={() => setFormMode('save-as')}
          >
            {t('GridView.saveAsSharedView')}
          </ActionRow>
          {views.currentSharedViewId && currentCanEdit && views.isModified ? (
            <ActionRow
              icon={<CheckIcon className="size-4" />}
              testId="grid-view-save-current"
              disabled={isViewActionDisabled}
              title={views.isLoading ? t('Common.loading') : undefined}
              onClick={() => void views.saveChangesToCurrentSharedView()}
            >
              {t('GridView.saveChangesToCurrentView')}
            </ActionRow>
          ) : null}
          {views.currentSharedViewId && views.hasPersonalDraft ? (
            <ActionRow
              icon={<ArrowUUpLeftIcon className="size-4" />}
              testId="grid-view-restore-saved"
              disabled={isViewActionDisabled}
              title={views.isLoading ? t('Common.loading') : undefined}
              onClick={() => void views.restoreSavedView()}
            >
              {t('GridView.restoreSavedView')}
            </ActionRow>
          ) : null}
          <ActionRow
            icon={<ArrowCounterClockwiseIcon className="size-4" />}
            testId="grid-view-restore-default"
            disabled={isViewActionDisabled || isViewingDefault}
            title={restoreSkopDefaultTitle}
            onClick={() => void views.applySystemDefaultView()}
          >
            {restoreSystemDefaultLabel}
          </ActionRow>
        </div>
        {!views.canCreateSharedView ? (
          <p className="px-3 pt-1.5 text-xs leading-4 text-slate-500">
            {t('GridView.readonlyHelp')}
          </p>
        ) : null}
      </div>

      <ViewFormDialog
        mode={formMode}
        defaultName={formMode === 'edit-meta' ? editView?.name : ''}
        defaultDescription={
          formMode === 'edit-meta' ? (editView?.description ?? '') : ''
        }
        canSetDefaultTeam={views.canSetDefaultSharedView}
        onClose={() => {
          setFormMode(null)
          setEditViewId(null)
        }}
        onSubmit={(params) => {
          if (formMode === 'save-as') {
            return views.saveAsSharedView(params)
          }

          const viewId = editView?.id ?? views.currentSharedViewId
          if (!viewId) return Promise.resolve()

          return views.updateSharedViewMeta({
            viewId,
            name: params.name,
            description: params.description,
          })
        }}
      />

      <DeleteViewDialog
        viewName={deleteView?.name ?? null}
        onClose={() => setDeleteViewId(null)}
        onConfirm={() =>
          deleteViewId
            ? views.deleteSharedView(deleteViewId)
            : Promise.resolve()
        }
      />
    </div>
  )
}

export function GridViewButton({ className }: { className?: string }) {
  const t = useTranslations()
  const views = useGridViews()
  const { api, gridIsReady } = useGridApi()
  const preventFocusSteal = usePreventGridFocusSteal()
  const [open, setOpen] = useState(false)
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen && api && gridIsReady) {
        api.setSideBarVisible(false)
      }
      setOpen(nextOpen)
    },
    [api, gridIsReady],
  )

  if (!views?.enabled) return null

  const hasReadError = views.readState === 'error'
  const labelText = hasReadError
    ? t('GridView.viewsUnavailable')
    : getCurrentViewLabel({
        currentViewKind: views.currentViewKind,
        currentSharedViewName: views.currentSharedViewName,
        currentSystemViewName: views.currentSystemViewName,
        viewSource: views.viewSource,
        t,
      })

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="action"
          data-testid="grid-view-trigger"
          data-grid-view-read-state={views.readState}
          data-grid-view-write-state={views.writeState}
          className={cx(
            'min-w-0 max-w-[190px] gap-1.5 overflow-hidden xl:max-w-[220px]',
            className,
          )}
          onMouseDown={preventFocusSteal}
          disabled={views.isLoading}
          title={labelText}
          aria-label={labelText}
        >
          <EyeIcon aria-hidden className="size-5 shrink-0" />
          <span
            className={cx(
              'min-w-0 max-w-[128px] truncate xl:max-w-[160px]',
              hasReadError ? 'text-amber-900' : 'hidden xl:block',
            )}
          >
            {labelText}
          </span>
          {views.isModified ? (
            <span
              className="size-2 rounded-full bg-primary-500"
              aria-label={t('GridView.modified')}
            />
          ) : null}
          {views.writeState === 'conflict' ? (
            <span
              className="size-2 rounded-full bg-amber-500"
              aria-label={t('GridView.preferenceConflict')}
            />
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto !p-0">
        <GridViewMenuItems onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}
